/** Keep the real project URL/storage key, while moving browser HTTP traffic to Pages. */
export function proxySupabaseUrl(url: string, projectUrl: string, origin: string): string {
  const target = new URL(url);
  if (target.origin !== new URL(projectUrl).origin) return url;
  return `${origin}/api/supabase${target.pathname}${target.search}${target.hash}`;
}

/** Requests whose Pages step does more than relay: the Google account check and OAuth consent redirects. */
export function needsProxy(url: string): boolean {
  const { pathname, searchParams } = new URL(url);
  return (
    (pathname === '/auth/v1/token' && searchParams.get('grant_type') === 'id_token') ||
    pathname === '/auth/v1/authorize' ||
    pathname.startsWith('/auth/v1/oauth/')
  );
}

export type Route = 'direct' | 'proxy';

export interface SupabaseRoute {
  current(): Promise<Route>;
  /** supabase.co failed on this network: use Pages for the rest of this load and the next day. */
  fail(): void;
}

type RouteStore = Pick<Storage, 'getItem' | 'setItem'>;
const ROUTE_KEY = 'cs_supabase_route';
const PROXY_FOR_MS = 86_400_000;

/**
 * Supabase answers from India in about 0.2 s, against 0.5–1 s through Pages (served from Europe for India).
 * Some networks may not reach supabase.co, so one quick check decides, and a failure is remembered for a day.
 */
export function supabaseRoute(
  projectUrl: string,
  anonKey: string,
  store: RouteStore | null,
  transport: typeof fetch = fetch,
  now = Date.now,
): SupabaseRoute {
  const remember = (route: Route) => {
    try {
      store?.setItem(ROUTE_KEY, JSON.stringify({ route, at: now() }));
    } catch {
      /* storage blocked */
    }
  };
  let route: Promise<Route>;
  let saved: { route?: Route; at?: number } = {};
  try {
    saved = JSON.parse(store?.getItem(ROUTE_KEY) || '{}');
  } catch {
    /* unreadable */
  }
  if (saved.route === 'proxy' && now() - (saved.at || 0) < PROXY_FOR_MS) route = Promise.resolve('proxy');
  else
    route = transport(`${projectUrl}/auth/v1/health`, {
      headers: { apikey: anonKey },
      signal: AbortSignal.timeout(3_000),
    }).then(
      (response) => (response.ok ? 'direct' : 'proxy') as Route,
      () => 'proxy' as Route,
    ).then((result) => {
      remember(result);
      return result;
    });
  return {
    current: () => route,
    fail() {
      route = Promise.resolve('proxy');
      remember('proxy');
    },
  };
}

const alwaysProxy: SupabaseRoute = { current: async () => 'proxy', fail() {} };

// Never pass a Request with a body as another Request's init: that copies its body as a stream, and older
// Chrome/WebView (seen on Chrome 124) rejects it without `duplex`. supabase-js sends auth calls as a URL and
// a plain init, but database queries and RPCs as a Request, so that body is read and sent as bytes instead.
export function supabaseFetch(
  projectUrl: string,
  origin: string,
  transport: typeof fetch = fetch,
  route: SupabaseRoute = alwaysProxy,
): typeof fetch {
  const projectOrigin = new URL(projectUrl).origin;
  return async (input, init) => {
    let build: (url: string) => Request;
    let url: string;
    if (!(input instanceof Request)) {
      url = input instanceof URL ? input.href : input;
      build = (target) => new Request(target, init);
    } else {
      const request = init ? new Request(input, init) : input;
      url = request.url;
      const body =
        request.method === 'GET' || request.method === 'HEAD' ? undefined : await request.arrayBuffer();
      build = (target) =>
        new Request(target, {
          method: request.method,
          headers: request.headers,
          body,
          signal: request.signal,
          credentials: request.credentials,
          cache: request.cache,
          redirect: request.redirect,
        });
    }
    if (new URL(url).origin !== projectOrigin) return transport(build(url));
    const proxied = () => transport(build(proxySupabaseUrl(url, projectUrl, origin)));
    if (needsProxy(url) || (await route.current()) === 'proxy') return proxied();
    const direct = build(url);
    try {
      return await transport(direct);
    } catch (error) {
      // A caller's abort is not a network problem; anything else means supabase.co is unreachable here.
      if (direct.signal?.aborted) throw error;
      route.fail();
      return proxied();
    }
  };
}
